import { CubeContext } from '@cubejs-client/react';
import dayjs from 'dayjs';
import _, { some } from 'lodash';
import { useContext, useMemo } from 'react';
import { useAppDispatch } from '../../../../../app/hooks';
import {
  BlueberryFeatures,
  BlueberryFlowerStageFeatures,
  CubeDimensionEnum,
  CubeMeasureEnum,
  FeatureEnum,
} from '../../../../../services/data/impl/cube/cubeSlice';
import { Block } from '../../../../../services/entities/impl/blfApi/schemas/block';
import { useTranslation } from '../../../../../services/translation/hooks/useTranslation';
import { useFilterCustomerSummary } from '../../../../customerSummary/hooks/useFilterCustomerSummary';
import {
  RanchPageViewEnum,
  setRanchPageView,
} from '../../../../ranches/ranchesSlice';
import { exportBlockRowsideEstimatedCountCSV } from '../../../../ranches/utils/exportBlockRowsideEstimatedCountCSV';
import {
  setCurrBlockByID,
  setCurrRanchByID,
  setCurrScanByID,
} from '../../../../select/selectSlice';
export type BlueberryTableCount = {
  value: number | null;
  percentage: number | null;
  perPlant: number | null;
};

export interface BlockMeasureResult {
  id: string;
  scanDate: string;
  ranchName: string;
  blockName: string;
  [FeatureEnum.blueberry_Blue]: BlueberryTableCount;
  [FeatureEnum.blueberry_Green]: BlueberryTableCount;
  [FeatureEnum.blueberry_Purple]: BlueberryTableCount;
  [FeatureEnum.blueberry_Yellow]: BlueberryTableCount;
  [FeatureEnum.flower]: BlueberryTableCount;
  totalEstimatedFlowerCount: number | null;
  totalFlowersPerPlant: number | null;
  totalEstimatedBerryCount: number | null;
  totalBerriesPerPlant: number | null;
  details?: () => void;
  download?: () => void;
}

type Props = {
  blockSummary: ReturnType<typeof useFilterCustomerSummary>;
  blocksFiltered: Block[] | undefined;
};

/**
 * Calculate the blueberry measures based on the block summary.
 *
 * @param {Props} blockSummary - The block summary data.
 * @return {BlockMeasureResult[]} An array of block measure results.
 */
export const useCalculateBlueberryMeasures = ({
  blocksFiltered,
  blockSummary,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { cubejsApi } = useContext(CubeContext);

  const blocksToDisplay: BlockMeasureResult[] = useMemo(() => {
    if (!blockSummary || !blocksFiltered) return [];

    const flowerFeatures = [
      ...BlueberryFlowerStageFeatures,
      FeatureEnum.flower,
    ];

    const allBlueberryFeatures = [
      ...BlueberryFeatures,
      ...flowerFeatures,
      FeatureEnum.blueberry,
    ];

    const groupedData = Object.entries(
      _.groupBy(
        blockSummary
          .filter((el) =>
            allBlueberryFeatures.find(
              (f) => f === el[CubeDimensionEnum.className]
            )
          )
          .map((el) => ({
            ...el,
            id: `${el[CubeDimensionEnum.detectionFactBlockId]}-${
              el[CubeDimensionEnum.detectionFactScanId]
            }`,
          })),
        (el) => el.id
      )
    ).map(([key, entry]) => {
      const blueberryEstimatedCounts = Object.fromEntries(
        allBlueberryFeatures.map((feature) => [
          feature,
          entry.find((el) => el[CubeDimensionEnum.className] === feature)?.[
            CubeMeasureEnum.detectionFactEstimatedCount
          ] || null,
        ])
      );

      const relatedBlock: Block | undefined = blocksFiltered.find(
        (block) => block.id === entry[0][CubeDimensionEnum.detectionFactBlockId]
      );

      const plantSpacing = relatedBlock?.plantSpacing;

      // Total distance traveled must come from the class "blueberry".  This is the only class that runs for every point in the scan.  The other classes only run for points that are detected as that class.  So if we use the total distance traveled from the other classes, we will be missing the distance traveled for points that are not detected as that class.
      let totalDistanceTraveled: number | null = null;
      // Check if "blueberry" class exists in the block summary for this scan.  It should exist in all new scans, but not in all old scans.

      const foundBlueberryClass = entry.find(
        (el) => el[CubeDimensionEnum.className] === FeatureEnum.blueberry
      );

      if (foundBlueberryClass) {
        totalDistanceTraveled =
          foundBlueberryClass[CubeMeasureEnum.detectionFactTraveledDistance] ||
          null;
      }

      // If total distance traveled is null, calculate it by taking the highest distance traveled from the other classes as a temporary backup.  This is not ideal because it will be missing the distance traveled for points that are not detected as that class.  This is just to get through the Hortifruit demo. REMOVE THIS WHEN WE HAVE THE TOTAL DISTANCE TRAVELED FOR THE BLUEBERRY CLASS IN ALL SCANS.
      if (!totalDistanceTraveled) {
        let highestDistanceTraveled: number | null = null;
        entry.forEach((el) => {
          const currDistanceTraveled =
            el[CubeMeasureEnum.detectionFactTraveledDistance];
          if (currDistanceTraveled && highestDistanceTraveled === null) {
            highestDistanceTraveled = currDistanceTraveled;
          } else if (
            currDistanceTraveled &&
            highestDistanceTraveled &&
            currDistanceTraveled > highestDistanceTraveled
          ) {
            highestDistanceTraveled = currDistanceTraveled;
          }
        });

        totalDistanceTraveled = highestDistanceTraveled;
      }

      // prevent zero division
      // IMPORTANT: FOR NON BLUEBERRY MEASURES WE MAY OR MAY NOT MULTIPLY BY 2 DEPENDING ON TRELLIS STRUCTURE SO WE'LL BE ADDING A NEW BLOCK DETAIL ITEM THAT HAS THE OPTION TO MULTIPLY BY 2 OR NOT.  WE ALSO NEED TO SHOW THIS EQUATION AND WHETHER THE MULTIPLICATION BY 2 IS HAPPENING OR NOT IN THE UI SO THE USER KNOWS WHAT'S GOING ON.
      const feetToMetersFactor = 0.3048;
      let totalEstimatedPlantsCaptured: number | null = null;
      if (totalDistanceTraveled && plantSpacing) {
        totalEstimatedPlantsCaptured =
          totalDistanceTraveled / (plantSpacing * feetToMetersFactor * 2);
      }

      const blueberryAvgEstimatedCountsPerPlant = Object.fromEntries(
        [...BlueberryFeatures, ...flowerFeatures].map((feature) => {
          const relatedColorEstimatedCount = blueberryEstimatedCounts[feature];

          return [
            feature,
            relatedColorEstimatedCount && totalEstimatedPlantsCaptured
              ? relatedColorEstimatedCount / totalEstimatedPlantsCaptured
              : null,
          ];
        })
      );

      // Filter out the "blueberry" class so only the color classes remain.
      let blueberryEstimatedCountsFiltered: {
        [key: string]: number | null;
      } = {};

      if (blueberryEstimatedCounts) {
        const entries = Object.entries(blueberryEstimatedCounts);
        const filteredEntries = entries.filter(
          ([key]) =>
            ![FeatureEnum.blueberry].includes(key as FeatureEnum) &&
            ![FeatureEnum.flower].includes(key as FeatureEnum)
        );
        blueberryEstimatedCountsFiltered = Object.fromEntries(filteredEntries);
      }

      // Only sum the berry features to get the total estimated berry count
      const blueberryEstimatedCountsBerriesOnly = Object.fromEntries(
        Object.entries(blueberryEstimatedCountsFiltered).filter(([key]) =>
          BlueberryFeatures.includes(key as FeatureEnum)
        )
      );

      const totalEstimatedBerryCount = Object.values(
        blueberryEstimatedCountsBerriesOnly
      ).reduce((acc: number, el) => {
        if (el === null) return acc;
        return acc + el;
      }, 0);

      const blueberryAvgEstimatedCountsPerPlantBerriesOnly = Object.fromEntries(
        Object.entries(blueberryAvgEstimatedCountsPerPlant).filter(([key]) =>
          BlueberryFeatures.includes(key as FeatureEnum)
        )
      );

      let totalBerriesPerPlant: number | null = null;
      if (some(Object.values(blueberryAvgEstimatedCountsPerPlantBerriesOnly))) {
        totalBerriesPerPlant = Object.values(
          blueberryAvgEstimatedCountsPerPlantBerriesOnly
        ).reduce((acc: number, el) => {
          if (el === null) return acc;
          return acc + el;
        }, 0);
      }

      // Only sum the flower features to get the total estimated flower count
      const hasFlowerStages = BlueberryFlowerStageFeatures.some(
        (flowerStage) => blueberryEstimatedCountsFiltered[flowerStage]
      );

      let totalEstimatedFlowerCount: number | null = null;
      let totalFlowersPerPlant: number | null = null;

      if (hasFlowerStages) {
        const blueberryEstimatedCountsFlowersOnly = Object.fromEntries(
          Object.entries(blueberryEstimatedCountsFiltered).filter(([key]) =>
            BlueberryFlowerStageFeatures.includes(key as FeatureEnum)
          )
        );

        totalEstimatedFlowerCount = Object.values(
          blueberryEstimatedCountsFlowersOnly
        ).reduce((acc: number, el) => {
          if (el === null) return acc;
          return acc + el;
        }, 0);

        const blueberryAvgEstimatedCountsPerPlantFlowerStagesOnly =
          Object.fromEntries(
            Object.entries(blueberryAvgEstimatedCountsPerPlant).filter(
              ([key]) =>
                BlueberryFlowerStageFeatures.includes(key as FeatureEnum)
            )
          );

        if (
          some(
            Object.values(blueberryAvgEstimatedCountsPerPlantFlowerStagesOnly)
          )
        ) {
          totalFlowersPerPlant = Object.values(
            blueberryAvgEstimatedCountsPerPlantFlowerStagesOnly
          ).reduce((acc: number, el) => {
            if (el === null || el === undefined) return acc;
            return acc + el;
          }, 0);
        }
      } else {
        totalEstimatedFlowerCount =
          blueberryEstimatedCounts[FeatureEnum.flower] || 0;

        totalFlowersPerPlant =
          blueberryAvgEstimatedCountsPerPlant[FeatureEnum.flower] || 0;
      }

      const measures = Object.fromEntries([
        ...BlueberryFeatures.map((feature) => [
          feature as keyof BlockMeasureResult,
          {
            value: blueberryEstimatedCountsFiltered[feature],
            // typescript should be able to figure out that blueberryEstimatedCounts[feature] is not null
            percentage:
              blueberryEstimatedCountsFiltered[feature] &&
              totalEstimatedBerryCount
                ? (blueberryEstimatedCountsFiltered[feature]! /
                    totalEstimatedBerryCount) *
                  100
                : null,
            perPlant: blueberryAvgEstimatedCountsPerPlant[feature],
          },
        ]),
        ...BlueberryFlowerStageFeatures.map((feature) => [
          feature as keyof BlockMeasureResult,
          {
            value: blueberryEstimatedCountsFiltered[feature],
            // typescript should be able to figure out that blueberryEstimatedCounts[feature] is not null
            percentage:
              blueberryEstimatedCountsFiltered[feature] &&
              totalEstimatedFlowerCount
                ? (blueberryEstimatedCountsFiltered[feature]! /
                    totalEstimatedFlowerCount) *
                  100
                : null,
            perPlant: blueberryAvgEstimatedCountsPerPlant[feature],
          },
        ]),
      ]) as {
        [FeatureEnum.blueberry_Blue]: BlueberryTableCount;
        [FeatureEnum.blueberry_Green]: BlueberryTableCount;
        [FeatureEnum.blueberry_Purple]: BlueberryTableCount;
        [FeatureEnum.blueberry_Yellow]: BlueberryTableCount;
        [FeatureEnum.flower]: BlueberryTableCount;
        [FeatureEnum.flower_closed]: BlueberryTableCount;
        [FeatureEnum.flower_open]: BlueberryTableCount;
        [FeatureEnum.flower_petalfall]: BlueberryTableCount;
      };

      return {
        id: key,
        scanDate: entry[0][CubeDimensionEnum.scanDate],
        ranchName: entry[0][CubeDimensionEnum.ranchName],
        blockName: entry[0][CubeDimensionEnum.blockName],
        ...measures,
        totalEstimatedFlowerCount,
        totalEstimatedBerryCount,
        totalFlowersPerPlant,
        totalBerriesPerPlant,
        details: () => {
          dispatch(
            setCurrRanchByID(
              entry[0][CubeDimensionEnum.detectionFactRanchId] as FeatureEnum
            )
          );
          dispatch(
            setCurrBlockByID(entry[0][CubeDimensionEnum.detectionFactBlockId])
          );
          dispatch(
            setCurrScanByID(entry[0][CubeDimensionEnum.detectionFactScanId])
          );
          dispatch(setRanchPageView(RanchPageViewEnum.blockDetail));
        },
        download: () => {
          exportBlockRowsideEstimatedCountCSV({
            blockName: entry[0][CubeDimensionEnum.blockName],
            cubejsApi,
            scanId: entry[0][CubeDimensionEnum.detectionFactScanId],
            scanDate: dayjs(entry[0][CubeDimensionEnum.scanDate]).format(
              'MM-DD-YYYY'
            ),
            serviceName: entry[0][CubeDimensionEnum.modelName] as FeatureEnum,
            serviceVersion: entry[0][CubeDimensionEnum.modelVersion],
            t: t,
          });
        },
      };
    });

    return groupedData;
  }, [blockSummary, blocksFiltered, dispatch, t]);

  return blocksToDisplay;
};
